import React, { useState, useEffect, useCallback } from 'react';
import { Upload, FileText, Trash2, Download, Eye, Loader2, CheckCircle, Clock, XCircle, FolderOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
    { value: 'financials', label: 'Financial Statements' },
    { value: 'tax', label: 'Tax Documents' },
    { value: 'receipts', label: 'Receipts & Invoices' },
    { value: 'bas', label: 'BAS Documents' },
    { value: 'payroll', label: 'Payroll Records' },
    { value: 'other', label: 'Other' }
];

const STATUS_BADGES = {
    pending: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: 'Pending Review' },
    reviewed: { icon: Eye, color: 'bg-blue-100 text-blue-700', label: 'Reviewed' },
    approved: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Approved' },
    rejected: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Rejected' }
};

export const DocumentUpload = ({ onUploadSuccess }) => {
    const [uploading, setUploading] = useState(false);
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [category, setCategory] = useState('');
    const [description, setDescription] = useState('');

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size (10MB max)
            if (file.size > 10 * 1024 * 1024) {
                toast.error('File too large. Maximum size is 10MB');
                return;
            }
            setSelectedFile(file);
            setShowUploadDialog(true);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile || !category) {
            toast.error('Please select a file and category');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('category', category);
        if (description) formData.append('description', description);

        try {
            const token = localStorage.getItem('finmar_token');
            const response = await fetch(`${API}/documents/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Upload failed');
            }

            toast.success('Document uploaded successfully');
            setShowUploadDialog(false);
            setSelectedFile(null);
            setCategory('');
            setDescription('');
            if (onUploadSuccess) onUploadSuccess();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-finmar-gold transition-colors">
                <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg"
                    data-testid="file-input"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                    <p className="text-sm font-medium text-finmar-navy">Click to upload documents</p>
                    <p className="text-xs text-slate-500 mt-1">PDF, DOC, XLS, CSV, Images (max 10MB)</p>
                </label>
            </div>

            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                <DialogContent data-testid="upload-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-finmar-gold" />
                            Upload Document
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {selectedFile && (
                            <div className="bg-slate-50 p-3 rounded-lg flex items-center gap-3">
                                <FileText className="w-8 h-8 text-finmar-navy" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-finmar-navy truncate">{selectedFile.name}</p>
                                    <p className="text-xs text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label>Category <span className="text-red-500">*</span></Label>
                            <Select value={category} onValueChange={setCategory}>
                                <SelectTrigger data-testid="category-select">
                                    <SelectValue placeholder="Select document category" />
                                </SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map(cat => (
                                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Description (optional)</Label>
                            <Textarea
                                placeholder="Add any notes about this document..."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={3}
                                data-testid="description-input"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpload}
                            disabled={uploading || !category}
                            className="bg-finmar-gold hover:bg-finmar-gold/90 text-finmar-navy"
                            data-testid="upload-btn"
                        >
                            {uploading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4 mr-2" />
                                    Upload
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export const DocumentList = ({ refreshTrigger, showTable = false }) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterCategory, setFilterCategory] = useState('all');

    const fetchDocuments = useCallback(async () => {
        try {
            const token = localStorage.getItem('finmar_token');
            const url = filterCategory === 'all' 
                ? `${API}/documents/my`
                : `${API}/documents/my?category=${filterCategory}`;
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setDocuments(data.documents || []);
            }
        } catch (error) {
            console.error('Failed to fetch documents:', error);
        } finally {
            setLoading(false);
        }
    }, [filterCategory]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments, refreshTrigger]);

    const handleDownload = async (doc) => {
        try {
            const token = localStorage.getItem('finmar_token');
            const response = await fetch(`${API}/documents/${doc.document_id}/download`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = doc.filename;
                a.click();
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            toast.error('Failed to download document');
        }
    };

    const handleDelete = async (doc) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;
        
        try {
            const token = localStorage.getItem('finmar_token');
            const response = await fetch(`${API}/documents/${doc.document_id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                toast.success('Document deleted');
                fetchDocuments();
            } else {
                throw new Error('Failed to delete');
            }
        } catch (error) {
            toast.error('Failed to delete document');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-finmar-gold" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-finmar-navy">Your Documents</h3>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-48">
                        <SelectValue placeholder="Filter by category" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {CATEGORIES.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {documents.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No documents uploaded yet</p>
                </div>
            ) : showTable ? (
                /* Table View */
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-slate-200">
                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Document</th>
                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Category</th>
                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Uploaded</th>
                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {documents.map(doc => {
                                const status = STATUS_BADGES[doc.status] || STATUS_BADGES.pending;
                                const StatusIcon = status.icon;
                                
                                return (
                                    <tr key={doc.document_id} className="border-b border-slate-50 hover:bg-slate-50">
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-3">
                                                <FileText className="w-8 h-8 text-finmar-navy" />
                                                <div>
                                                    <p className="font-medium text-finmar-navy truncate max-w-[200px]">{doc.filename}</p>
                                                    <p className="text-xs text-slate-400">{(doc.file_size / 1024).toFixed(1)} KB</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="text-sm capitalize">{doc.category}</span>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${status.color}`}>
                                                <StatusIcon className="w-3 h-3" />
                                                {status.label}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-sm text-slate-500">
                                            {new Date(doc.uploaded_at).toLocaleDateString()}
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-1">
                                                <Button variant="ghost" size="icon" onClick={() => handleDownload(doc)}>
                                                    <Download className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => handleDelete(doc)} className="text-red-500 hover:text-red-700">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                /* Card View */
                <div className="space-y-2">
                    {documents.map(doc => {
                        const status = STATUS_BADGES[doc.status] || STATUS_BADGES.pending;
                        const StatusIcon = status.icon;
                        
                        return (
                            <div
                                key={doc.document_id}
                                className="flex items-center gap-4 p-4 bg-white border border-slate-100 rounded-lg hover:border-finmar-gold/30 transition-colors"
                                data-testid={`document-${doc.document_id}`}
                            >
                                <FileText className="w-10 h-10 text-finmar-navy flex-shrink-0" />
                                
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-finmar-navy truncate">{doc.filename}</p>
                                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                                        <span className="capitalize">{doc.category}</span>
                                        <span>•</span>
                                        <span>{(doc.file_size / 1024).toFixed(1)} KB</span>
                                        <span>•</span>
                                        <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                
                                <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${status.color}`}>
                                    <StatusIcon className="w-3 h-3" />
                                    <span>{status.label}</span>
                                </div>
                                
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDownload(doc)}
                                        data-testid={`download-${doc.document_id}`}
                                    >
                                        <Download className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDelete(doc)}
                                        className="text-red-500 hover:text-red-700"
                                        data-testid={`delete-${doc.document_id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default DocumentUpload;
